import { defineStore } from 'pinia';

import type { Workshop } from '@/types/api/predictiveModels/workshops';
/* TYPES */
import type {
  AiMatrixDrawerParams,
  CongratsModalParams,
  ExternalRedirectionModalParams,
  ExtractDataCampaignDrawerParams,
  NetworkActionsDrawerParams,
  OverlayName,
  RejectionEmailDrawerParams,
  SendResultsDrawerParams,
  ShowReportsDrawerParams,
  VoiceDrawerParams
} from '@/types/overlays/overlays.types';

type Overlay = {
  open: boolean;
  params: any;
  canClose?: boolean;
};
type State = {
  overlays: Record<string, Overlay>;
};
export const initialState = (): State => ({
  overlays: {
    /* DIALOGS */
    externalRedirectionModal: {
      open: false,
      params: undefined as unknown as ExternalRedirectionModalParams
    },
    congratsModal: {
      open: false,
      params: undefined as unknown as CongratsModalParams
    },
    shareCampaignToManagerModal: { open: false, params: undefined },
    /* SIDE PANELS */
    groups: { open: false, params: undefined },
    acceptedContactsFilters: { open: false, params: undefined },
    pendingContactsFilters: { open: false, params: undefined },
    devPlansFilters: { open: false, params: undefined },
    rejectionEmailDrawer: {
      open: false,
      params: undefined as unknown as RejectionEmailDrawerParams
    },
    campaignProfile: { open: false, params: undefined },
    campaignStepUsers: { open: false, params: undefined },
    addCampaignStep: { open: false, params: undefined },
    showReports: {
      open: false,
      params: undefined as unknown as ShowReportsDrawerParams
    },
    activeCampaignsFilters: { open: false, params: undefined },
    pausedCampaignsFilters: { open: false, params: undefined },
    activeCampaignsTabFilters: { open: false, params: undefined },
    rejectedCampaignsTabFilters: { open: false, params: undefined },
    campaignStepper: { open: false, params: undefined },
    pmFilters_benchmark: { open: false, params: undefined },
    pmFilters_personalized: { open: false, params: undefined },
    competencyFrameworkDrawer: { open: false, params: undefined },
    extractCampaignDataDrawer: {
      open: false,
      params: undefined as unknown as ExtractDataCampaignDrawerParams
    },
    teamAnalysisExtractDrawer: {
      open: false,
      params: undefined as unknown as object
    },
    // Navbar
    nav: { open: false, params: undefined },
    batches: { open: false, params: undefined },

    /* PROFILE */
    profileDrawer: { open: false, params: undefined },

    /* AI PM */
    aiMatrix: {
      open: false,
      params: undefined as unknown as AiMatrixDrawerParams
    },
    aiTestModel: {
      open: false,
      params: undefined as unknown as { pm: Workshop }
    },
    editWorkshop: {
      open: false,
      params: undefined as unknown as { uuid: string }
    },
    /* CONTACTS */
    sendResults: {
      open: false,
      params: undefined as unknown as SendResultsDrawerParams
    },
    addToCampaign: {
      open: false,
      params: undefined as unknown as NetworkActionsDrawerParams
    },
    addToGroup: {
      open: false,
      params: undefined as unknown as NetworkActionsDrawerParams
    },
    manageAccess: {
      open: false,
      params: undefined as unknown as NetworkActionsDrawerParams
    },
    /* VOICE */
    manageVoiceSkills: {
      open: false,
      params: undefined as unknown as VoiceDrawerParams
    },
    /* CHATBOT */
    chatbot: { open: false, params: undefined }
  }
});

export const useOverlayStore = defineStore('overlay', {
  state: initialState,
  getters: {
    /**
     * Return the closable state
     *
     * @param state State of overlay store
     * @param { OverlayName} overlayName Key name for overlay
     *
     * @returns
     */
    isClosable(state) {
      return (overlayName: OverlayName) => {
        return state.overlays[overlayName]?.canClose ?? true;
      };
    },
    /**
     * Return open state of a specific overlay
     * @param state State of overlay store
     * @param { OverlayName} overlayName Key name for overlay
     *
     * @returns Function to fing overlay open state
     */
    getOverlayState(state) {
      return (overlayName: OverlayName): boolean => state.overlays[overlayName]?.open || false;
    },
    /**
     * Return params given to a drawer
     * @param state State of overlay store
     * @returns  { OverlayName} overlayName Key name for overlay
     */
    getOverlayParams(state) {
      return (overlayName: OverlayName) => {
        return state.overlays[overlayName]?.params;
      };
    },
    /**
     * Allows to know if there is at least one overlay open in the app
     * @param state
     * @returns {boolean} is there at least one overlay open
     */
    isOneOverlayOpen(state) {
      const openOverlays = Object.values(state.overlays).find(overlay => {
        return !!overlay.open;
      });
      return !!openOverlays;
    }
  },
  actions: {
    /**
     * Update the closable state
     */
    setClosable(key: OverlayName, isClosable: boolean) {
      this.overlays[key].canClose = isClosable;
    },
    /**
     * Update the <html> overflow
     */
    setHTMLOverflow() {
      const hideOverflow = this.isOneOverlayOpen; // If at least one open overlay, hide html overflow
      const htmlElement = window.document.getElementsByTagName('html')[0];
      if (htmlElement) {
        htmlElement.style.overflow = hideOverflow ? 'hidden' : 'hidden scroll';
      }
    },
    /**
     * Create a drawer. Used when creating a drawer with a dynamic name
     * @param {string} key - drawer's name
     */
    createDrawer(key) {
      this.overlays[key] = {
        params: undefined,
        open: false,
        canClose: true
      };
    },
    /**
     * Toggle a given overlay
     * @param {OverlayName} key - overlay name
     * @param {any} params - params to send to overlay if any
     * @param {boolean | null} openingState - if a specific opening / close state and not a toggle
     */
    toggle(key: OverlayName, params: any = undefined, openingState: boolean | null = null) {
      const updatedOpeningState = openingState === null ? !this.overlays[key].open : openingState;
      this.overlays[key].params = params;
      this.overlays[key].open = updatedOpeningState;
    },
    /**
     * reset params for a given overlay
     * @param {OverlayName} key - overlay name for which to reset params
     */
    resetParams(key: OverlayName) {
      this.overlays[key].params = undefined;
    },
    /**
     * close a given overlay
     * @param {OverlayName} key - overlay name
     */
    close(key: OverlayName) {
      this.overlays[key].params = undefined;
      this.overlays[key].open = false;
    }
  }
});
